import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components';
import Users, { type CalendarTokenStatus } from '../../../services/Users';

type CalendarSubscriptionPanelProps = {
    isAdmin: boolean;
};

const STORAGE_KEY = 'yoda.calendar_ics_token';

type StoredCalendarToken = {
    prefix: string;
    token: string;
};

/**
 * Persist plaintext ICS token in this browser so copy buttons keep working after refresh.
 * Invalidated when prefix no longer matches the active server token (regen/revoke).
 */
function readStoredToken(prefix?: string | null): string | null {
    if (!prefix)
        return null;
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw) as StoredCalendarToken;
        if (parsed?.prefix === prefix && typeof parsed.token === 'string' && parsed.token.startsWith(prefix))
            return parsed.token;
    } catch (_err) {
        /* ignore corrupt storage */
    }
    return null;
}

function writeStoredToken(prefix: string, token: string) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ prefix, token } satisfies StoredCalendarToken));
}

function clearStoredToken() {
    localStorage.removeItem(STORAGE_KEY);
}

function maskSecretInUrl(url: string): string {
    return url.replace(/([?&]token=)[^&]*/i, '$1••••••••');
}

function toWebcal(httpsUrl: string): string {
    return httpsUrl.replace(/^https?:\/\//i, 'webcal://');
}

function buildUserHttpsUrl(userId: string, token: string): string {
    return `${window.location.origin}/calendar/users/${encodeURIComponent(userId)}.ics?token=${encodeURIComponent(token)}`;
}

function buildTeamHttpsUrl(token: string): string {
    return `${window.location.origin}/calendar/team.ics?token=${encodeURIComponent(token)}`;
}

/**
 * Own-profile ICS calendar subscription: generate/revoke token and copy webcal URL.
 */
function CalendarSubscriptionPanel({ isAdmin }: CalendarSubscriptionPanelProps) {
    const { t } = useTranslation();
    const [status, setStatus] = useState<CalendarTokenStatus | null>(null);
    const [knownToken, setKnownToken] = useState<string | null>(null);
    const [justCreated, setJustCreated] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);

    const syncKnownToken = useCallback((next: CalendarTokenStatus, plaintext?: string | null) => {
        const prefix = next.token_prefix || null;
        const active = !!(next.has_token && !next.revoked);
        if (!active || !prefix) {
            clearStoredToken();
            setKnownToken(null);
            setJustCreated(false);
            return;
        }
        if (plaintext) {
            writeStoredToken(prefix, plaintext);
            setKnownToken(plaintext);
            return;
        }
        setKnownToken(readStoredToken(prefix));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const next = await Users.getCalendarToken();
            setStatus(next);
            syncKnownToken(next);
            setJustCreated(false);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : t('common.error'));
        } finally {
            setLoading(false);
        }
    }, [syncKnownToken, t]);

    useEffect(() => {
        load();
    }, [load]);

    const copyText = async (value?: string | null) => {
        if (!value) {
            setError(t('users.calendar_copy_unavailable'));
            return;
        }
        try {
            await navigator.clipboard.writeText(value);
            setError(null);
        } catch (_err) {
            setError(t('users.calendar_copy_failed'));
        }
    };

    const createOrRegenerate = async () => {
        setBusy(true);
        setError(null);
        try {
            const result = await Users.createCalendarToken();
            setStatus(result);
            syncKnownToken(result, result.token);
            setJustCreated(true);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : t('common.error'));
        } finally {
            setBusy(false);
        }
    };

    const revoke = async () => {
        setBusy(true);
        setError(null);
        try {
            const next = await Users.revokeCalendarToken();
            clearStoredToken();
            setKnownToken(null);
            setJustCreated(false);
            setStatus(next);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : t('common.error'));
        } finally {
            setBusy(false);
        }
    };

    const active = !!(status?.has_token && !status?.revoked);
    const userId = status?.calendar_token?.user
        || status?.https_url?.match(/\/calendar\/users\/([^./?]+)/)?.[1]
        || null;

    const httpsUrl = useMemo(() => {
        if (!active || !knownToken)
            return null;
        if (userId)
            return buildUserHttpsUrl(userId, knownToken);
        if (status?.https_url)
            return status.https_url.replace(/([?&]token=)[^&]*/i, `$1${encodeURIComponent(knownToken)}`);
        return null;
    }, [active, knownToken, userId, status?.https_url]);

    const webcalUrl = httpsUrl ? toWebcal(httpsUrl) : null;

    const teamHttpsUrl = useMemo(() => {
        if (!active || !isAdmin || !knownToken)
            return null;
        return buildTeamHttpsUrl(knownToken);
    }, [active, isAdmin, knownToken]);

    const teamWebcalUrl = teamHttpsUrl ? toWebcal(teamHttpsUrl) : null;

    const displayHttps = httpsUrl
        ? maskSecretInUrl(httpsUrl)
        : (userId
            ? maskSecretInUrl(buildUserHttpsUrl(userId, 'hidden'))
            : null);
    const displayWebcal = displayHttps ? toWebcal(displayHttps) : null;
    const displayTeamWebcal = teamWebcalUrl
        ? maskSecretInUrl(teamWebcalUrl)
        : (isAdmin && active
            ? maskSecretInUrl(buildTeamHttpsUrl('hidden')).replace(/^https?:\/\//i, 'webcal://')
            : null);

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {t('users.calendar_title')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    {t('users.calendar_desc')}
                </p>
            </div>

            {error && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                </div>
            )}

            {loading ? (
                <p className="text-sm text-gray-600 dark:text-gray-300">{t('common.loading')}</p>
            ) : (
                <>
                    <div className="rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 space-y-2">
                        <p className="text-sm text-gray-700 dark:text-gray-200">
                            {active
                                ? t('users.calendar_status_active', { prefix: status?.token_prefix || '…' })
                                : t('users.calendar_status_inactive')}
                        </p>
                        {status?.last_used_at && (
                            <p className="text-xs text-gray-500">
                                {t('users.calendar_last_used')}: {new Date(status.last_used_at).toLocaleString()}
                            </p>
                        )}
                        <div className="flex flex-wrap gap-2 pt-1">
                            <Button
                                title={active ? t('users.calendar_regenerate') : t('users.calendar_generate')}
                                onClick={createOrRegenerate}
                                loading={busy}
                                color="primary"
                                icon="plus"
                            />
                            {active && (
                                <Button
                                    title={t('users.calendar_revoke')}
                                    onClick={revoke}
                                    loading={busy}
                                    color="danger"
                                    icon="delete"
                                />
                            )}
                            <Button
                                title={t('users.calendar_refresh')}
                                onClick={load}
                                loading={loading}
                                color="default"
                                icon="sync"
                            />
                        </div>
                    </div>

                    {justCreated && knownToken && (
                        <div className="rounded border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 p-3">
                            <p className="text-sm font-semibold text-yellow-800">{t('users.calendar_created_once')}</p>
                            <p className="text-sm text-yellow-700 mb-2">{t('users.calendar_created_copy_now')}</p>
                            <code className="block break-all text-xs mb-2 text-yellow-900 dark:text-yellow-100">{knownToken}</code>
                            <Button title={t('users.calendar_copy_token')} onClick={() => copyText(knownToken)} color="secondary" icon="copy" />
                        </div>
                    )}

                    {active && (
                        <div className="rounded border border-blue-200 bg-blue-50 dark:bg-blue-950/30 p-3 space-y-2">
                            <p className="text-sm font-semibold text-blue-800">{t('users.calendar_subscribe_title')}</p>
                            <p className="text-sm text-blue-700">{t('users.calendar_subscribe_desc')}</p>
                            {!knownToken && (
                                <p className="text-sm text-amber-800">{t('users.calendar_url_hidden')}</p>
                            )}
                            {knownToken && !justCreated && (
                                <p className="text-sm text-blue-700">{t('users.calendar_urls_masked')}</p>
                            )}
                            <label className="block text-xs font-medium text-blue-900 dark:text-blue-100">{t('users.calendar_webcal_url')}</label>
                            <code className="block break-all text-xs text-blue-900 dark:text-blue-100 bg-white/70 dark:bg-gray-900/50 rounded px-2 py-1">
                                {displayWebcal || '…'}
                            </code>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    title={t('users.calendar_copy_webcal')}
                                    onClick={() => copyText(webcalUrl)}
                                    color="primary"
                                    icon="copy"
                                />
                                <Button
                                    title={t('users.calendar_copy_https')}
                                    onClick={() => copyText(httpsUrl)}
                                    color="secondary"
                                    icon="copy"
                                />
                            </div>
                            {isAdmin && (
                                <div className="pt-2 border-t border-blue-200 mt-2">
                                    <label className="block text-xs font-medium text-blue-900 dark:text-blue-100 mb-1">
                                        {t('users.calendar_team_webcal_url')}
                                    </label>
                                    <code className="block break-all text-xs text-blue-900 dark:text-blue-100 bg-white/70 dark:bg-gray-900/50 rounded px-2 py-1 mb-2">
                                        {displayTeamWebcal || '…'}
                                    </code>
                                    <Button
                                        title={t('users.calendar_copy_team_webcal')}
                                        onClick={() => copyText(teamWebcalUrl)}
                                        color="primary"
                                        icon="copy"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default CalendarSubscriptionPanel;
